/**
 * src/api/printing/adapters/PrinterAdapter.ts
 *
 * Dispatch vers les services hardware existants (Strangler Fig — invariant 4.B.5).
 *
 * Responsabilités :
 *   - Sélectionner le service printer approprié (Sunmi AIDL, Bluetooth, Network, Web)
 *   - Déléguer l'impression effective aux adapters legacy src/services/hardware/printer/
 *   - Mapper les résultats legacy → PrintResult / PrintError
 *   - Wrapper discovery vers PrinterDevice[] typé
 *
 * Ce fichier NE réécrit PAS les adapters legacy.
 * Les bridges natifs (SunmiPlugin, BluetoothBridge) restent inchangés.
 *
 * Invariants :
 *   4.B.2 — aucun import supabase direct ici
 *   4.B.4 — 0 any
 *   4.B.5 — Strangler Fig : wrap, pas réécriture
 *   4.A.16 — mentions NF525 déléguées à PrintService legacy (ce module ne formate pas les tickets)
 */

import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { getPrinterService } from '@/services/hardware/printer';
import { discoverPrinters } from '@/services/hardware/printer/discovery';
import type { DiscoveredPrinter } from '@/services/hardware/printer/discovery';
import type { PrinterDevice, PrintResult } from '../types';
import { noPrinterConnectedError, printerError, printerTimeoutError } from '../errors';
import type { PrintError } from '../errors';

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** Timeout d'impression en ms (5s pour Sunmi AIDL, 10s pour BT/Network). */
const PRINT_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Helpers internes
// ---------------------------------------------------------------------------

/**
 * Mapper DiscoveredPrinter → PrinterDevice (types API publics).
 * Remplit les champs inconnus avec des valeurs par défaut sûres.
 */
function discoveredToPrinterDevice(d: DiscoveredPrinter): PrinterDevice {
  return {
    id: [d.transport, d.ip ?? d.mac ?? d.name].join('|'),
    name: d.name,
    connectionType: mapTransportToConnectionType(d.transport),
    printerType: 'receipt',
    btMacAddress: d.mac ?? null,
    ipAddress: d.ip ?? null,
    port: d.port ?? 9100,
    registerId: null,
    isDefault: false,
    paperWidth: 80,
    charPerLine: 48,
  };
}

function mapTransportToConnectionType(
  transport: DiscoveredPrinter['transport'],
): PrinterDevice['connectionType'] {
  switch (transport) {
    case 'bluetooth':
      return 'bluetooth';
    case 'network':
      return 'network';
    case 'usb':
      return 'usb';
    default:
      return 'network';
  }
}

/**
 * Détecte si un Sunmi AIDL bridge est disponible dans la WebView.
 * Utilisé pour sélectionner la route d'impression la plus directe.
 */
function hasSunmiBridge(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as typeof window & Record<string, unknown>;
  return typeof w['SunmiPrinter'] !== 'undefined' || typeof w['SunmiInnerPrinter'] !== 'undefined';
}

// ---------------------------------------------------------------------------
// PrinterAdapter
// ---------------------------------------------------------------------------

export const PrinterAdapter = {
  /**
   * Imprime des bytes ESC/POS bruts via le meilleur service disponible.
   *
   * Ordre de priorité (identique à getPrinterService) :
   *   1. Sunmi AIDL (bridge WebView injecté par APK)
   *   2. Sunmi SunmiPlugin (Capacitor)
   *   3. WebPrinter (Edge Function cloud)
   *
   * Pour Bluetooth et Network, PrintService legacy gère déjà le dispatch
   * via findPrinter() + sendViaBluetooth/sendViaNetwork.
   * Ce wrapper délègue sans remplacer cette logique.
   *
   * @param documentType Type de document (pour logging uniquement)
   * @param printFn      Fonction appelant le service legacy approprié (ex: printSaleReceipt)
   */
  async dispatchPrint(
    documentType: string,
    printFn: () => Promise<boolean>,
  ): Promise<Result<PrintResult, PrintError>> {
    const startAt = Date.now();

    // Vérification préliminaire : au moins un service disponible ?
    const service = getPrinterService();
    const isAvailable = await service.isAvailable().catch(() => false);
    if (!isAvailable && !hasSunmiBridge()) {
      return err(noPrinterConnectedError());
    }

    // Timeout wrapper pour éviter les blocages sur bridge natif lent
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
    }, PRINT_TIMEOUT_MS);

    try {
      const success = await printFn();
      clearTimeout(timeoutId);

      if (timedOut) {
        return err(printerTimeoutError());
      }

      if (!success) {
        return err(printerError('PRINT_FAILED', `Échec impression (${documentType})`));
      }

      const route = hasSunmiBridge() ? 'sunmi_aidl' : 'fallback';

      return ok({
        route,
        deviceName: hasSunmiBridge() ? 'Sunmi AIDL' : 'WebPrinter',
        durationMs: Date.now() - startAt,
        logId: null,
        isDuplicate: false,
      } satisfies PrintResult);
    } catch (e) {
      clearTimeout(timeoutId);
      const message = e instanceof Error ? e.message : String(e);
      return err(printerError('PRINT_EXCEPTION', message));
    }
  },

  /**
   * Wrapper discovery : retourne les imprimantes découvertes en PrinterDevice[].
   * Délègue entièrement à discoverPrinters() legacy.
   */
  async listDevices(timeoutMs = 8000): Promise<Result<PrinterDevice[], PrintError>> {
    try {
      const discovered = await discoverPrinters({ timeoutMs });
      return ok(discovered.map(discoveredToPrinterDevice));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return err(printerError('DISCOVERY_FAILED', message));
    }
  },
};
